/* Where a buyer is — browser timezone + optional home state (TKT-00157).
   Staff see it on the buyer profile to know the buyer's local time before a
   call. Backend: app/services/user_location.py. */
import { api } from "@/lib/api";

export interface UserLocation {
  timezone: string | null;
  home_state_id: number | null;
  home_state: string | null;
  home_state_timezone: string | null;
}

/** The zone if this browser's Intl accepts it, else null. */
export function safeTimeZone(tz: string | null | undefined): string | null {
  if (!tz) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}

/** "Central Time", "Pacific Time" … (falls back to the IANA name). */
export function timeZoneLabel(tz: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longGeneric" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? tz;
  } catch {
    return tz;
  }
}

/** "4:12 PM" in the given zone. */
export function localTimeIn(tz: string, at: Date = new Date()): string {
  return at.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" });
}

/**
 * Send this browser's timezone for the signed-in buyer — once per tab session,
 * and again only if it changes (travel, a move). Silent and best-effort: it's a
 * convenience for staff, never something that may interrupt the buyer.
 */
export function reportBrowserTimezone(userId: string): void {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return;
  }
  if (!tz) return;
  const key = `bl-tz-reported:${userId}`;
  try {
    if (window.sessionStorage.getItem(key) === tz) return;
  } catch {
    /* storage blocked — still report */
  }
  api
    .put("/profile/me/location", { timezone: tz })
    .then(() => {
      try {
        window.sessionStorage.setItem(key, tz!);
      } catch {
        /* ignore */
      }
    })
    .catch(() => {});
}
